import {
  Alert,
  AlertDescription,
  Button,
  Icon,
  Stack,
  Text,
} from '@eluve/components';
import { useTranslation } from '@eluve/i18n';

import { PdfPreview } from './PdfPreview';

const MAX_VISIBLE_FIELDS = 5;

const truncatedFieldList = (fields: string[], moreSuffix: string): string =>
  fields.slice(0, MAX_VISIBLE_FIELDS).join(', ') +
  (fields.length > MAX_VISIBLE_FIELDS ? ` ${moreSuffix}` : '');

const FillNotice: React.FC<{
  warning?: boolean;
  children: React.ReactNode;
}> = ({ warning, children }) => (
  <Alert variant={warning ? 'warning' : undefined}>
    <Icon name={warning ? 'AlertTriangle' : 'Info'} />
    <AlertDescription>{children}</AlertDescription>
  </Alert>
);

export type PdfViewerContentProps = {
  /** A URL the browser can render in <object> (signed GCS URL or blob URL). */
  fileUrl: string;
  fileName: string;
  onDownload: () => void;
  skippedFields?: string[];
  alteredFields?: string[];
  usedAppearanceFallback?: boolean;
  excludedHumanOnlyFields?: string[];
};

/**
 * Read-only filled-PDF view: filename + download, fill warning callouts, and an
 * inline preview. Shared by the in-flight fill result (PdfFillTabContent) and
 * the persisted document tab (PdfDocumentTabContent).
 */
export const PdfViewerContent: React.FC<PdfViewerContentProps> = ({
  fileUrl,
  fileName,
  onDownload,
  skippedFields = [],
  alteredFields = [],
  usedAppearanceFallback = false,
  excludedHumanOnlyFields = [],
}) => {
  const { t } = useTranslation();
  const fieldList = (fields: string[]) =>
    truncatedFieldList(
      fields,
      t('appointmentTools.documents.moreFields', {
        count: fields.length - MAX_VISIBLE_FIELDS,
      }),
    );

  return (
    <Stack className="gap-[26px]" fullHeight align="stretch">
      <Stack justify="between" gap={2} direction="horizontal">
        <Text weight="semibold" className="truncate">
          {fileName}
        </Text>
        <Button size="sm" onClick={onDownload}>
          <Icon name="Download" size="auto" />
          {t('appointmentTools.documents.download')}
        </Button>
      </Stack>
      {skippedFields.length > 0 && (
        <FillNotice warning>
          {skippedFields.length === 1
            ? t('appointmentTools.documents.skippedFieldsOne', {
                count: skippedFields.length,
                fields: fieldList(skippedFields),
              })
            : t('appointmentTools.documents.skippedFieldsMany', {
                count: skippedFields.length,
                fields: fieldList(skippedFields),
              })}
        </FillNotice>
      )}
      {alteredFields.length > 0 && (
        <FillNotice warning>
          {alteredFields.length === 1
            ? t('appointmentTools.documents.alteredFieldsOne', {
                count: alteredFields.length,
                fields: fieldList(alteredFields),
              })
            : t('appointmentTools.documents.alteredFieldsMany', {
                count: alteredFields.length,
                fields: fieldList(alteredFields),
              })}
        </FillNotice>
      )}
      {usedAppearanceFallback && (
        <FillNotice warning>
          {t('appointmentTools.documents.appearanceFallback')}
        </FillNotice>
      )}
      {excludedHumanOnlyFields.length > 0 && (
        <FillNotice>
          {excludedHumanOnlyFields.length === 1
            ? t('appointmentTools.documents.humanOnlyFieldsOne', {
                count: excludedHumanOnlyFields.length,
              })
            : t('appointmentTools.documents.humanOnlyFieldsMany', {
                count: excludedHumanOnlyFields.length,
              })}
        </FillNotice>
      )}
      {/* react-pdf (canvas) renders inline even for cross-origin signed URLs; a
        native <object> renders blank / force-downloads them in Chrome. */}
      <PdfPreview file={fileUrl} className="max-h-[70vh]" />
    </Stack>
  );
};
